import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import { ReviewAudio } from '../components/ReviewAudio';
import { getDevelopment, ApiError, type Development, type DevelopmentClass } from '../lib/api';
import { routePath } from '../routes/route';
import { tapProps } from '../lib/tap';
import './Career.css';

export interface CareerDevelopmentProps {
  tenantId: string;
  /** R4 (Actionable Everywhere): drill a theme/feedback rollup down to the
   *  records behind it. Optional so existing tests mount without it. */
  onNavigate?: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const ROLE_LABEL: Record<string, string> = {
  coach: 'coach',
  agent: 'agent',
  manager: 'manager',
  self: 'self',
  other: 'reviewer',
};

function pct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function fieldText(rec: DevelopmentClass, name: string): string | null {
  const v = rec.fields[name];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

// A short, human "when" — the Development cards want a calm date, not a clock.
function whenDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// B7 CAREER / DEVELOPMENT — replaces the B4b placeholder. The insight rollup off
// REAL coach/reviewer notes (GET /api/actor/development): recurring-theme tiles
// (the deterministic n-gram detector, ">= N reviews"), the latest tape feedback
// with its synced audio, the callback-rate progress bar (the SAME computation as
// Stats), and current classes. Matches the demo's `development` screen; every
// block is honest about an empty first-run (no fabricated pattern).
export function CareerDevelopment({ tenantId, onNavigate }: CareerDevelopmentProps) {
  // A recurring theme is detected across reviews we don't expose individual ids
  // for, so a theme tile drills SECTION-LEVEL to the Auditions the reviews
  // attach to. (The feedback cards below aren't wrapped as doorways on purpose:
  // each already holds its own live handle — the ReviewAudio player — and a
  // card-level navigate would hijack those controls.)
  const toAuditions = onNavigate
    ? () => onNavigate(routePath({ screen: 'auditions', stage: 'all' }))
    : undefined;
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<Development | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getDevelopment(tenantId);
        if (cancelled) return;
        setData(d);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Development" line="One moment…" />;
  }
  if (state === 'error' || !data) {
    return (
      <PlaceholderScreen eyebrow="Development" line="Couldn't load your development." soft={errorMessage ?? undefined} />
    );
  }

  const { recentFeedback, recurringThemes, callbackProgress, classes } = data;
  const hasAnything = recentFeedback.length > 0 || recurringThemes.length > 0 || classes.length > 0;

  // A fresh actor with no feedback yet — an inviting empty, not a blank screen.
  if (!hasAnything && callbackProgress.total === 0) {
    return (
      <PlaceholderScreen
        eyebrow="Development"
        line="Development insights arrive with your coach feedback."
        soft="Send a tape for notes, and patterns across your tapes will surface here."
      />
    );
  }

  return (
    <div class="career">
      <div class="career__eyebrow">Development</div>

      {recurringThemes.length > 0 ? (
        <>
          <div class="say career__say">Across your tapes, a pattern.</div>
          {recurringThemes.slice(0, 4).map((theme) => (
            <div class="tile tile--neutral" key={theme.phrase} data-testid="theme-tile" {...tapProps(toAuditions)}>
              <div class="tile__ic" aria-hidden="true">
                ↻
              </div>
              <div class="tile__body">
                <div class="tile__tt">Recurring note: &ldquo;{theme.phrase}&rdquo;</div>
                <div class="tile__sub">
                  across {theme.count} {theme.count === 1 ? 'review' : 'reviews'}
                </div>
              </div>
              <div class="tile__word">Focus</div>
            </div>
          ))}
        </>
      ) : (
        recentFeedback.length > 0 && (
          <div class="career__note" data-testid="themes-empty">
            One theme repeated across two or more notes surfaces here — not enough shared notes yet.
          </div>
        )
      )}

      <div class="career__grp">Latest tape · feedback</div>
      {recentFeedback.length === 0 ? (
        <div class="career__note" data-testid="feedback-empty">No notes in yet.</div>
      ) : (
        recentFeedback.map((fb) => {
          const role = fb.reviewerRole ? ROLE_LABEL[fb.reviewerRole] ?? fb.reviewerRole : null;
          const when = whenDate(fb.submittedAt);
          return (
            <div class="career__card" style="cursor:default" key={fb.reviewId} data-testid="feedback-card">
              <div class="career__card-top">
                <span class="career__card-nm">{fb.reviewer ?? 'A reviewer'}</span>
                {(role || when) && (
                  <span class="career__card-sub">{[role, when].filter(Boolean).join(' · ')}</span>
                )}
              </div>
              {fb.snippet && <div class="dev-snippet">{fb.snippet}</div>}
              <ReviewAudio tenantId={tenantId} reviewId={fb.reviewId} />
            </div>
          );
        })
      )}

      <div class="career__grp">Progress · callback rate</div>
      {callbackProgress.total === 0 ? (
        <div class="career__note" data-testid="progress-empty">
          Your callback rate builds as your auditions come through.
        </div>
      ) : (
        <div data-testid="callback-progress">
          <div class="prog" role="img" aria-label={`Callback rate ${pct(callbackProgress.callbackRate)}`}>
            <i style={`width:${Math.round(callbackProgress.callbackRate * 100)}%`} />
          </div>
          <div class="career__note" style="font-size:var(--text-xs)">
            {pct(callbackProgress.callbackRate)} of {callbackProgress.total}{' '}
            {callbackProgress.total === 1 ? 'audition' : 'auditions'} reached a callback
          </div>
        </div>
      )}

      {classes.length > 0 && (
        <>
          <div class="career__grp">Classes</div>
          {classes.map((cls) => (
            <div class="career__kv" key={cls.id} data-testid="class-row">
              <span class="career__kv-k">
                {fieldText(cls, 'name') ?? fieldText(cls, 'title') ?? cls.fields['institution'] ?? 'Class'}
              </span>
              <span class="career__kv-v">
                {fieldText(cls, 'cadence') ?? fieldText(cls, 'frequency') ?? fieldText(cls, 'coach') ?? 'current'}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
